import { Dispatch, SetStateAction } from 'react';
import {
  defaultInitialFields,
  INITIAL_FIELDS_BY_CATEGORY,
  IInitialFieldConfig,
  TOOL_INSPECTION_FIELD_DATA_TYPE,
  TOOL_INSPECTION_FIELD_ID,
  TToolInspectionFieldDataType,
  TToolInspectionParsedValue
} from 'constants/toolInspection.constants';
import type {
  IInspectionField,
  IToolInspectionFieldReq,
  IToolInspectionImageReq,
  TToolInspectionImageSlot
} from 'types/suscription/toolInspection.types';
import { IDeviceSyncCompleteReq } from 'types/wms/device-sync.types';
import { LOCATION_PLACE_TYPE } from 'types/services';

const parseFieldValue = (
  type: TToolInspectionFieldDataType,
  value: string | null
): TToolInspectionParsedValue | null => {
  if (value === null) return null;

  switch (type) {
    case TOOL_INSPECTION_FIELD_DATA_TYPE.BOOLEAN:
      return value === 'true';
    case TOOL_INSPECTION_FIELD_DATA_TYPE.JSON:
      try {
        const parsed: unknown = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed as string[];
        }
        if (parsed && typeof parsed === 'object') {
          return parsed as Record<string, unknown>;
        }
        return {};
      } catch {
        return {};
      }
    default:
      return value;
  }
};

export const mapFieldsToState = (fields: IInspectionField[], category: string): Record<number, IInitialFieldConfig> => {
  const defaults = INITIAL_FIELDS_BY_CATEGORY[category] || defaultInitialFields;

  if (!fields.length) return defaults;

  const result: Record<number, IInitialFieldConfig> = { ...defaults };

  fields.forEach(field => {
    const defaultFieldLabel = result[field.id]?.label;

    const parsedValue = parseFieldValue(field.dataType, field.value);
    const defaultFieldValue = result[field.id]?.value;
    const value = parsedValue || defaultFieldValue;

    result[field.id] = {
      id: field.id,
      label: defaultFieldLabel,
      dataType: field.dataType,
      value
    };
  });

  const commentsFieldId = TOOL_INSPECTION_FIELD_ID.COMMENTS;

  return {
    ...result,
    [commentsFieldId]: {
      ...defaults[commentsFieldId],
      value: result[commentsFieldId]?.value ?? ''
    }
  };
};

export const mapFieldsToFieldsReq = (fields: Record<number, IInitialFieldConfig>): IToolInspectionFieldReq[] => {
  return Object.values(fields).map(f => {
    if (f.value == null) {
      return { inspectionFieldId: f.id, value: null };
    }
    const v = f.value;
    const processedValue = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return { inspectionFieldId: f.id, value: processedValue };
  });
};

export const mapSessionCompleteResToImagesReq = (evidence: IDeviceSyncCompleteReq): IToolInspectionImageReq[] => {
  return evidence.payload.photos.map(photo => ({
    id: photo.id ?? null,
    imageSlot: photo.name as TToolInspectionImageSlot,
    url: photo.url
  }));
};

export const resolveVideoCableSelection = (prev: string[], next: string[], noneValue: string): string[] => {
  if (next.length === 0) return [noneValue];
  const nextHasNone = next.includes(noneValue);
  if (!nextHasNone) return next;

  const prevHadNone = prev.includes(noneValue);
  if (prevHadNone && next.length > 1) {
    return next.filter(v => v !== noneValue);
  }
  return [noneValue];
};

export const getFieldValue = <T>(fields: Record<number, IInitialFieldConfig>, id: number): T => fields[id]?.value as T;

export const getFieldLabel = (fields: Record<number, IInitialFieldConfig>, id: number) => fields[id]?.label;

type TSelectLikeOption = Readonly<{
  value: string | number;
  label: string;
}>;

export const formatSingleSelectValueAsLabel = (
  value: string | number | null | undefined,
  options: ReadonlyArray<TSelectLikeOption>
): string => {
  if (value == null || value === '') return '';
  const found = options.find(option => String(option.value) === String(value));
  return found?.label ?? String(value);
};

export const formatMultiSelectValuesAsLabels = (
  storedValues: string[] | null | undefined,
  options: ReadonlyArray<TSelectLikeOption>,
  separator = ', '
): string => {
  const values = storedValues ?? [];
  if (!values.length) return '';

  return values
    .map(value => options.find(option => String(option.value) === String(value))?.label ?? value)
    .filter(label => label !== '')
    .join(separator);
};

export interface ICountdownTickParams {
  enabled: boolean;
  setRemainingSeconds: Dispatch<SetStateAction<number>>;
  onExpired: () => void;
  resetToSeconds: number;
  tickIntervalMs?: number;
}

export const createCountdownTick = (params: ICountdownTickParams): (() => void) => {
  const { enabled, setRemainingSeconds, onExpired, resetToSeconds, tickIntervalMs = 1000 } = params;

  if (!enabled) {
    return () => undefined;
  }

  const intervalId = window.setInterval(() => {
    setRemainingSeconds(prev => {
      if (prev <= 1) {
        onExpired();
        return resetToSeconds;
      }
      return prev - 1;
    });
  }, tickIntervalMs);

  return () => {
    window.clearInterval(intervalId);
  };
};

interface IToolInformationAlertMessages {
  updateMessage: string;
  ubicationMessage: string;
}

export const getToolInformationAlertMessages = (locationPlace: string | null): IToolInformationAlertMessages => {
  const updateMessage =
    'Esta vista muestra solo el estado actual de la herramienta. Para consultar reportes anteriores del Device Status Notification (DSN), dirígete al Historial.';

  const ubicationMessage =
    locationPlace === LOCATION_PLACE_TYPE.NUDOS
      ? 'Al modificar la información del equipo, los cambios se reflejan en WMS.'
      : 'Este equipo no está en bodega, por lo que no es posible actualizar su información.';

  return { updateMessage, ubicationMessage };
};
